import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity'
import { createReducer, on } from '@ngrx/store'
import { produce } from 'immer'

import {
  DataMapper,
  DataMappingType,
  ResourceNodeSlice,
} from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import { DataModelUiEntity } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import { ActionDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'

import { DataMappingActions, LocalAttributeStaging } from '../actions/data-mapping.actions'

export const dataMappingFeatureKey = 'dataMapping'

export type DataMappingState = {
  elementsMapping: {
    data: EntityState<DataMappingSourceState>
    loading: boolean
  }
  resourceNodes: EntityState<ResourceSlicesState>
  localActionDataMappers?: EntityState<LocalDataMapperState>
  testMappingResponse?: {
    data: Record<string, any>
    loading: boolean
  }
}

export type DataMappingSourceState = {
  elementFlowxUuid: ActionDefinition['flowxUuid']
  loading: boolean
  nodesSliceMappings: DataMapper[]
}

export type ResourceSlicesState = {
  resourceId: string
  nodeSlices: EntityState<ResourceNodeSlice>
  dataEntities: DataModelUiEntity[]
}

export type LocalDataMapperState = Partial<DataMapper> &
  LocalAttributeStaging & {
    type: DataMappingType
    elementFlowxUuid?: string
  }

export const actionsDataMappingStateAdapter: EntityAdapter<DataMappingSourceState> =
  createEntityAdapter<DataMappingSourceState>({
    selectId: (m: DataMappingSourceState) => m.elementFlowxUuid,
    sortComparer: false,
  })

export const resourceSlicesStateAdapter: EntityAdapter<ResourceSlicesState> =
  createEntityAdapter<ResourceSlicesState>({
    selectId: (m: ResourceSlicesState) => m.resourceId,
    sortComparer: false,
  })

export const resourceNodesSlicesAdapter: EntityAdapter<ResourceNodeSlice> =
  createEntityAdapter<ResourceNodeSlice>({
    selectId: (m: ResourceNodeSlice) => m.nodeFlowxUuid,
    sortComparer: false,
  })

export const localDataMappersStateAdapter: EntityAdapter<LocalDataMapperState> =
  createEntityAdapter<LocalDataMapperState>({
    selectId: (m: LocalDataMapperState) => m.targetElementId,
    sortComparer: false,
  })

export const initialState: DataMappingState = {
  elementsMapping: { data: actionsDataMappingStateAdapter.getInitialState(), loading: false },
  resourceNodes: resourceSlicesStateAdapter.getInitialState(),
  localActionDataMappers: localDataMappersStateAdapter.getInitialState(),
}
export const dataMappingReducer = createReducer(
  initialState,
  on(
    DataMappingActions.loadResourceNodesSlicesSuccess,
    (state, { resourceId, resourceNodesSlices, dataEntities }) =>
      produce(state, (draft) => {
        const existingResource = draft.resourceNodes.entities[resourceId]

        draft.resourceNodes = resourceSlicesStateAdapter.upsertOne(
          {
            resourceId,
            nodeSlices: existingResource
              ? resourceNodesSlicesAdapter.upsertMany(
                  resourceNodesSlices,
                  existingResource.nodeSlices
                )
              : resourceNodesSlicesAdapter.setAll(
                  resourceNodesSlices,
                  resourceNodesSlicesAdapter.getInitialState()
                ),
            dataEntities,
          },
          draft.resourceNodes
        )
      })
  ),

  on(DataMappingActions.clearResourceNodesSlices, (state, { resourceId }) =>
    produce(state, (draft) => {
      draft.resourceNodes = resourceSlicesStateAdapter.removeOne(resourceId, draft.resourceNodes)
    })
  ),
  on(DataMappingActions.cleanupResourceNodesSlices, (state) =>
    produce(state, (draft) => {
      draft.resourceNodes = resourceSlicesStateAdapter.removeAll(draft.resourceNodes)
    })
  ),

  on(DataMappingActions.loadMappingsForSelectedElement, (state) =>
    produce(state, (draft) => {
      draft.elementsMapping.loading = true
    })
  ),
  on(DataMappingActions.cancelLoadMappingsForSelectedElement, (state) =>
    produce(state, (draft) => {
      draft.elementsMapping.loading = false
    })
  ),
  on(
    DataMappingActions.loadMappingsSuccess,
    (state, { elementFlowxUuid, targetProcessNodesMappers }) =>
      produce(state, (draft) => {
        draft.elementsMapping.data = actionsDataMappingStateAdapter.addOne(
          {
            elementFlowxUuid,
            nodesSliceMappings: targetProcessNodesMappers,
            loading: false,
          },
          draft.elementsMapping.data
        )
        draft.elementsMapping.loading = false
      })
  ),
  on(DataMappingActions.loadMappingsFailure, (state) =>
    produce(state, (draft) => {
      draft.elementsMapping.loading = false
    })
  ),

  on(DataMappingActions.addLocalDataMapper, (state, { mapper, mappingType, elementFlowxUuid }) =>
    produce(state, (draft) => {
      draft.localActionDataMappers = localDataMappersStateAdapter.setOne(
        { ...mapper, type: mappingType, elementFlowxUuid },
        draft.localActionDataMappers
      )
    })
  ),

  on(DataMappingActions.updateLocalDataMapper, (state, { mapper }) =>
    produce(state, (draft) => {
      draft.localActionDataMappers = localDataMappersStateAdapter.updateOne(
        {
          id: mapper.targetElementId,
          changes: {
            mapping: mapper.mapping,
            sourceSlice: mapper.sourceSlice,
            useGraalVM: mapper.useGraalVM,
            pendingAttributes: mapper.pendingAttributes,
          },
        },
        draft.localActionDataMappers
      )
    })
  ),

  on(DataMappingActions.clearLocalDataMappers, (state) =>
    produce(state, (draft) => {
      draft.localActionDataMappers = localDataMappersStateAdapter.removeAll(
        draft.localActionDataMappers
      )
    })
  ),

  on(
    DataMappingActions.bulkCreateDataMappingsSuccess,
    (state, { elementFlowxUuid, actionMappers }) =>
      produce(state, (draft) => {
        const newTargetIds = new Set(actionMappers.map((m) => m.targetElementId))
        const existingMappings =
          draft.elementsMapping.data.entities[elementFlowxUuid]?.nodesSliceMappings || []

        draft.elementsMapping.data = actionsDataMappingStateAdapter.updateOne(
          {
            id: elementFlowxUuid,
            changes: {
              nodesSliceMappings: [
                ...existingMappings.filter((m) => !newTargetIds.has(m.targetElementId)),
                ...actionMappers,
              ],
            },
          },
          draft.elementsMapping.data
        )
        draft.localActionDataMappers = localDataMappersStateAdapter.removeMany(
          actionMappers.map((mapper) => mapper.targetElementId),
          draft.localActionDataMappers
        )
      })
  ),
  on(
    DataMappingActions.bulkUpdateDataMappingsSuccess,
    (state, { elementFlowxUuid, actionMappers }) =>
      produce(state, (draft) => {
        draft.elementsMapping.data = actionsDataMappingStateAdapter.updateOne(
          {
            id: elementFlowxUuid,
            changes: {
              nodesSliceMappings: draft.elementsMapping.data.entities[
                elementFlowxUuid
              ].nodesSliceMappings.map(
                (mapper) =>
                  actionMappers.find(
                    (updatedMapper) => updatedMapper.targetElementId === mapper.targetElementId
                  ) ?? mapper
              ),
            },
          },
          draft.elementsMapping.data
        )
        draft.localActionDataMappers = localDataMappersStateAdapter.removeMany(
          actionMappers.map((mapper) => mapper.targetElementId),
          draft.localActionDataMappers
        )
      })
  ),

  on(DataMappingActions.testMapping, (state) =>
    produce(state, (draft) => {
      draft.testMappingResponse = { data: {}, loading: true }
    })
  ),

  on(DataMappingActions.testMappingSuccess, (state, { response }) =>
    produce(state, (draft) => {
      draft.testMappingResponse = { data: response, loading: false }
    })
  ),
  on(DataMappingActions.testMappingFailure, (state) =>
    produce(state, (draft) => {
      draft.testMappingResponse = { data: {}, loading: false }
    })
  ),

  on(DataMappingActions.clearTestMappingResponse, (state) =>
    produce(state, (draft) => {
      draft.testMappingResponse = undefined
    })
  ),
  on(DataMappingActions.cleanupMappings, (state) =>
    produce(state, (draft) => {
      draft.elementsMapping.data = actionsDataMappingStateAdapter.removeAll(
        draft.elementsMapping.data
      )
    })
  )
)

const { selectAll: selectResourceNodesSlices } = resourceNodesSlicesAdapter.getSelectors()
export const selectAllResourceNodesSlices = selectResourceNodesSlices

const { selectAll: selectLocalDataMappings, selectEntities: selectLocalDataMappingsEntitiesMap } =
  localDataMappersStateAdapter.getSelectors()

export const selectAllLocalDataMappings = selectLocalDataMappings
export const selectLocalDataMappingsEntities = selectLocalDataMappingsEntitiesMap
